import { roundTo2 } from '@fastre/core/helperFunctions/number'
import { toCurrency } from '@fastre/core/helperFunctions/string'
import { InternalAgentStatement } from '@fastre/core/schemas/agentStatement'
import { EditRounded } from '@mui/icons-material'
import { IconButton, Table } from '@mui/joy'
import { useUserData } from 'auth'
import SortableHeader, { Sort } from 'listings/listingsTable/sortableHeader'
import { ascend, descend, sortWith } from 'ramda'
import { useState } from 'react'
import AgentStatementRow from './agentStatementRow'

export default function AgentStatementTable({
    items,
    onEdit,
}: {
    items: InternalAgentStatement[]
    onEdit?: (item: InternalAgentStatement) => void
}) {
    const { user } = useUserData()
    const [sort, setSort] = useState<Sort>({
        sort: 'date',
        sortDirection: 'asc',
    })

    const balance = roundTo2(
        items.reduce((acc, item) => acc + (item.type == 'debit' ? -item.amount : item.amount), 0),
    )

    const sortFunc = sort.sortDirection == 'asc' ? ascend : descend

    return (
        <Table>
            <thead>
                <tr>
                    <th style={{ width: '120px' }}>
                        <SortableHeader
                            sort={sort}
                            setSort={setSort}
                            sortString="date"
                        >
                            Date
                        </SortableHeader>
                    </th>
                    <th>Description</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>
                        <SortableHeader
                            sort={sort}
                            setSort={setSort}
                            sortString="debit"
                        >
                            Debit
                        </SortableHeader>
                    </th>
                    <th style={{ width: '140px', textAlign: 'right' }}>
                        <SortableHeader
                            sort={sort}
                            setSort={setSort}
                            sortString="credit"
                        >
                            Credit
                        </SortableHeader>
                    </th>
                    {user.permissions.includes('agentStatement.edit') && <th style={{ width: '40px' }}></th>}
                </tr>
            </thead>
            <tbody>
                {sortWith(
                    [sortFunc(x => x[sort.sort] ?? '')],
                    items.map(item => ({
                        ...item,
                        credit: item.type == 'credit' ? item.amount : 0,
                        debit: item.type == 'debit' ? item.amount : 0,
                    })),
                ).map(item => (
                    <AgentStatementRow
                        item={item}
                        key={item.id}
                    >
                        {user.permissions.includes('agentStatement.edit') && onEdit && (
                            <td>
                                <IconButton onClick={() => onEdit(item)}>
                                    <EditRounded />
                                </IconButton>
                            </td>
                        )}
                    </AgentStatementRow>
                ))}
                <tr style={{ fontSize: 16, height: '40px' }}>
                    <td></td>
                    <td style={{ paddingTop: 32, textAlign: 'right' }}>
                        <b>Total</b>
                    </td>
                    <td style={{ paddingTop: 32, textAlign: 'right' }}>
                        <b>
                            {toCurrency(
                                items
                                    .filter(item => item.type == 'debit')
                                    .reduce((acc, item) => acc + item.amount, 0),
                            )}
                        </b>
                    </td>
                    <td style={{ paddingTop: 32, textAlign: 'right' }}>
                        <b>
                            {toCurrency(
                                items
                                    .filter(item => item.type == 'credit')
                                    .reduce((acc, item) => acc + item.amount, 0),
                            )}
                        </b>
                    </td>
                    {user.permissions.includes('agentStatement.edit') && <td />}
                </tr>
                <tr style={{ fontSize: 20, height: '40px' }}>
                    <td></td>
                    <td style={{ paddingTop: 32, textAlign: 'right' }}>
                        <b>Current Balance</b>
                    </td>
                    <td></td>
                    <td style={{ fontSize: 16, paddingTop: 32, textAlign: 'right' }}>
                        <b>${balance}</b>
                    </td>
                    {user.permissions.includes('agentStatement.edit') && <td />}
                </tr>
            </tbody>
        </Table>
    )
}
